import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { PLAN_PRICES } from "../lib/planCatalog";

const PAGE_TITLE = "WizMate｜社長の毎日に、AI社長室を。";
const PAGE_DESCRIPTION =
  "WizMateは、相談・壁打ち・情報収集・企業リサーチを、AIアシスタント「メイ」に頼める経営者向けのAI社長室です。フリープランあり、月額3,300円から。";
const LINE_FRIEND_URL = "https://lin.ee/UDfXGPL";
const HERO_IMAGE_SRC = "/img/lp-hero-mei.jpg";
const ABOUT_IMAGE_SRC = "/img/lp-about-office.jpg";

// 経営者が一人で抱えがちな相談テーマ（Problem セクションのチップ表示用）。
const CONCERNS = [
  "売上",
  "価格",
  "集客",
  "契約",
  "資金繰り",
  "採用",
  "顧客対応",
  "情報収集",
  "雑務",
];

const PILLARS = [
  {
    tag: "CHAT",
    title: "メイとのAIチャット",
    body: "仕事の話から雑談まで。ちょっとした相談や調べものを、いつでもメイに頼めます。",
  },
  {
    tag: "ROOM",
    title: "AI壁打ちルーム",
    body: "メイが進行し、AI専門家メンバーが複数の視点で意見を出します。仮想経営会議、企画の壁打ち、専門家に相談する前の論点整理に使えます。",
  },
  {
    tag: "AGENT",
    title: "AIエージェント",
    body: "ニュース配信、レポート、企業リサーチなど、調べものや情報整理をAIエージェントにまとめて頼めます。",
  },
] as const;

const KABEUCHI_STEPS = [
  { step: "01", title: "相談内容を入力する", body: "いま気になっていることを、そのまま書き出します。" },
  { step: "02", title: "メイがテーマ・メンバーを提案", body: "論点に合うテーマと、AI専門家メンバーを組み立てます。" },
  { step: "03", title: "AI専門家が視点を出す", body: "複数のAIメンバーが、それぞれの立場から意見を出します。" },
  { step: "04", title: "メイが次の一手に整理", body: "出てきた意見を、判断しやすい形に整理します。" },
] as const;

type Feature = {
  title: string;
  desc?: string;
  points: readonly string[];
};

const FEATURES: readonly Feature[] = [
  {
    title: "通常チャット",
    desc: "仕事の話から雑談まで、気軽にメイへ。",
    points: ["ちょっとした検索も頼める", "仕事の話から雑談まで", "メイの話し方の好みも選べる"],
  },
  {
    title: "AI壁打ちルーム",
    points: ["複数のAIメンバーで論点を整理", "経営判断前のたたき台づくり", "論点整理レポート"],
  },
  {
    title: "ニュース・レポート配信",
    points: ["必要な情報を定期的に届けてもらう", "毎日／毎週／毎月", "LINE・メールで通知"],
  },
  {
    title: "企業リサーチ",
    points: ["詳細・売り込み・協業・リスクの視点で調査", "商談前・提携前・競合確認に", "用途別に整理して依頼できる"],
  },
  {
    title: "書類・メモ整理と連携機能",
    points: [
      "領収書やメモの整理を順次拡張",
      "Google連携・スケジュール連携なども今後対応予定",
      "社長室として任せられる範囲を広げていきます",
    ],
  },
];

const COMPARISON_ROWS = [
  {
    issue: "何を聞けばいいか分からない",
    general: "使い方やプロンプトを自分で考える必要がある",
    wizmate: "相談・壁打ち・調査・配信など、生成AIの使いどころを専用機能として用意",
  },
  {
    issue: "話が広がりすぎて論点を見失う",
    general: "情報量が多く、結局何を考えるべきか分かりにくい",
    wizmate: "AI壁打ちルームでは、メイが論点を整理し、選択肢で次に進める",
  },
  {
    issue: "一人で考えていると、必要な視点が足りているか不安",
    general: "役割や立場を自分で指定する必要がある",
    wizmate: "AI専門家と発言のスタンスを組み合わせて壁打ちできる",
  },
  {
    issue: "前と同じ感じで調査したいのに、毎回指定するのが面倒",
    general: "調査内容や観点を毎回入力して指定する必要がある",
    wizmate: "企業リサーチやレポートなど、専用機能のテンプレートを活用して頼める",
  },
  {
    issue: "相談相手も、調査を任せる相手も足りない",
    general: "会話はできても、会議や調査業務として使うには自分で設計が必要",
    wizmate: "あなた専用のAI社長室として、壁打ち会議や調査業務をまとめて頼める",
  },
] as const;

const AUDIENCE = [
  "一人社長 / 個人事業主",
  "小規模企業の経営者",
  "社内に相談相手が少ない経営者",
  "ChatGPTを契約したが使いこなせていない方",
  "新規事業や業務改善の壁打ち相手が欲しい方",
  "顧問契約までは重いと感じている方",
] as const;

// LP 上のプラン向け補足。価格・名称は planCatalog の PLAN_PRICES を正本として参照する。
const PLAN_AUDIENCE: Record<string, string> = {
  free: "まずはWizMateの雰囲気を試したい方へ",
  standard: "日々の相談・壁打ち・情報収集に使いたい方へ",
  executive: "壁打ち・企業調査・定期配信をしっかり使い込みたい方へ",
};

const SAFETY_POINTS = [
  "AIの出力は判断材料です。最終的な意思決定は、ご自身で行ってください。",
  "法律・税務・会計・労務・医療・金融・投資などは、必要に応じて専門家にご確認ください。",
  "スポーツ結果・株価・天気・ニュースなど、最新性が重要な情報については、未確認のまま断定しないよう設計しています。",
  "ご利用前に、利用規約・プライバシーポリシー・特定商取引法に基づく表記をご確認ください。",
] as const;

const FAQS = [
  {
    q: "無料で使えますか？",
    a: "フリープランがあります。利用回数には上限があります。",
  },
  {
    q: "ChatGPTと何が違いますか？",
    a: "WizMateは汎用AIではなく、経営者が相談・壁打ち・情報収集・企業リサーチを頼みやすい形に整えたAI社長室です。",
  },
  {
    q: "LINEだけでも使えますか？",
    a: "LINEではメイとの会話や通知を利用できます。AI壁打ちルームなど一部機能はWeb画面での利用を想定しています。",
  },
  {
    q: "途中で解約できますか？",
    a: "有料プランは契約管理画面から解約できます。日割り返金はありません。",
  },
  {
    q: "AIの回答は正確ですか？",
    a: "AIの出力は判断材料であり、正確性や最新性を保証するものではありません。重要な判断では一次情報や専門家への確認をおすすめします。",
  },
  {
    q: "特典コードはありますか？",
    a: "紹介やキャンペーン等で特典コードを発行する場合があります。条件はコード適用画面または管理画面で確認できます。",
  },
] as const;

const primaryButtonClass =
  "inline-flex items-center justify-center gap-2 rounded-2xl bg-[#7C5A4E] px-6 py-3.5 text-sm font-black text-white shadow-[0_10px_24px_rgba(124,90,78,0.28)] transition hover:bg-[#6D4D42] active:scale-[0.99]";
const secondaryButtonClass =
  "inline-flex items-center justify-center gap-2 rounded-2xl border border-[#D9C4B3] bg-white px-6 py-3.5 text-sm font-black text-[#7C5A4E] transition hover:bg-[#FCF1E8] active:scale-[0.99]";
const lineButtonClass =
  "inline-flex items-center justify-center gap-2 rounded-2xl bg-[#06C755] px-6 py-3.5 text-sm font-black text-white shadow-[0_10px_24px_rgba(6,199,85,0.24)] transition hover:bg-[#05b34d] active:scale-[0.99]";

function LineBadge() {
  return (
    <span className="flex h-5 w-9 items-center justify-center rounded bg-white text-[10px] font-black leading-none text-[#06C755]">
      LINE
    </span>
  );
}

function LineFriendLink({ className, children }: { className: string; children: React.ReactNode }) {
  return (
    <a href={LINE_FRIEND_URL} target="_blank" rel="noreferrer" className={className}>
      {children}
    </a>
  );
}

function PrimaryCtaGroup() {
  return (
    <div className="flex flex-wrap items-center gap-3">
      <Link to="/register" className={primaryButtonClass}>
        無料ではじめる
      </Link>
      <LineFriendLink className={lineButtonClass}>
        <LineBadge />
        LINEで友だち追加
      </LineFriendLink>
      <Link to="/plans" className={secondaryButtonClass}>
        料金を見る
      </Link>
    </div>
  );
}

function SectionEyebrow({ children }: { children: React.ReactNode }) {
  return <p className="text-xs font-black tracking-[0.18em] text-[#A67C52]">{children}</p>;
}

// 背景透過のWizMateマーク + ワードマーク。画像が読み込めなくても「WizMate」の文字は残す。
function LogoMark() {
  return (
    <>
      <img
        src="/img/wizmate-mark.png"
        alt="WizMate ロゴ"
        className="h-9 w-9 object-contain"
        onError={(e) => {
          e.currentTarget.style.display = "none";
        }}
      />
      <span className="text-lg font-black tracking-tight text-[#7C5A4E]">WizMate</span>
    </>
  );
}

export default function LandingPage() {
  const [heroImageFailed, setHeroImageFailed] = useState(false);
  const [aboutImageFailed, setAboutImageFailed] = useState(false);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = PAGE_TITLE;

    const meta = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    const previousDescription = meta?.getAttribute("content") ?? null;
    if (meta) {
      meta.setAttribute("content", PAGE_DESCRIPTION);
    }

    return () => {
      document.title = previousTitle;
      if (meta && previousDescription !== null) {
        meta.setAttribute("content", previousDescription);
      }
    };
  }, []);

  return (
    <div className="h-dvh overflow-x-hidden overflow-y-auto scroll-smooth bg-[radial-gradient(circle_at_top,#FFF6EE_0%,#FDF8F5_46%,#F6ECE4_100%)] text-[#4A3E39] antialiased">
      {/* ヘッダー（LP内スクロールコンテナに対して固定表示） */}
      <header className="sticky top-0 z-40 border-b border-[#EFE2D3] bg-[#FDF8F5]/85 backdrop-blur-md">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-5 py-3 md:px-8">
          <a href="#top" className="flex items-center gap-2.5">
            <LogoMark />
          </a>
          <nav aria-label="セクション" className="hidden items-center gap-6 text-sm font-bold text-[#6B5851] md:flex">
            <a href="#about" className="transition hover:text-[#7C5A4E]">
              できること
            </a>
            <a href="#features" className="transition hover:text-[#7C5A4E]">
              機能
            </a>
            <a href="#plans" className="transition hover:text-[#7C5A4E]">
              料金
            </a>
            <a href="#faq" className="transition hover:text-[#7C5A4E]">
              FAQ
            </a>
          </nav>
          <div className="flex items-center gap-2.5">
            <Link to="/login" className="hidden text-sm font-bold text-[#7C5A4E] hover:underline sm:inline">
              ログイン
            </Link>
            <Link
              to="/register"
              className="rounded-xl bg-[#7C5A4E] px-4 py-2 text-sm font-black text-white transition hover:bg-[#6D4D42]"
            >
              無料ではじめる
            </Link>
          </div>
        </div>
      </header>

      <main id="top" className="scroll-mt-24">
        {/* Hero */}
        <section className="mx-auto max-w-6xl px-5 pb-10 pt-12 md:px-8 md:pb-16 md:pt-16">
          <div className="grid items-center gap-10 md:grid-cols-[1.05fr_0.95fr]">
            <div>
              <SectionEyebrow>AI社長室 for 経営者</SectionEyebrow>
              <h1 className="mt-4 text-4xl font-black leading-[1.18] tracking-tight text-[#5D4037] md:text-5xl">
                社長の毎日に、
                <br />
                AI社長室を。
              </h1>
              <p className="mt-5 max-w-xl text-balance text-base font-bold leading-8 text-[#5D4D46]">
                WizMateは、社長の悩み・判断・調べもの・日々の雑務を、メイとAIチームに頼めるAI社長室です。
              </p>
              <p className="mt-4 max-w-xl text-sm leading-7 text-[#6B5851]">
                相談、壁打ち、情報収集、企業リサーチまで。
                <br />
                ひとりで抱えがちな経営のモヤモヤを、AIと一緒に整理できます。
              </p>
              <div className="mt-8">
                <PrimaryCtaGroup />
              </div>
              <p className="mt-4 text-sm font-bold text-[#9C867D]">フリープランあり。月額3,300円から。</p>
            </div>

            {/* Hero ビジュアル：画像が読み込めなくてもカードのグラデーションでレイアウトを保つ。 */}
            <div className="relative mx-auto w-full max-w-md">
              <div className="pointer-events-none absolute -right-6 -top-8 h-40 w-40 rounded-full bg-[#F4DED0] blur-3xl" />
              <div className="pointer-events-none absolute -bottom-10 -left-6 h-44 w-44 rounded-full bg-[#F8EBDD] blur-3xl" />
              <div className="relative overflow-hidden rounded-[2.2rem] border border-[#EADBC8] bg-[linear-gradient(135deg,#FFF7F1_0%,#F7E7DA_100%)] p-3 shadow-[0_24px_50px_rgba(166,124,82,0.20)]">
                <div className="relative aspect-square overflow-hidden rounded-[1.7rem] bg-[linear-gradient(135deg,#FBEFE4_0%,#F3DEC9_100%)]">
                  <div className="absolute inset-0 grid place-items-center px-6 text-center">
                    <span className="text-sm font-black tracking-[0.16em] text-[#B89A80]">WizMate</span>
                  </div>
                  {!heroImageFailed ? (
                    <img
                      src={HERO_IMAGE_SRC}
                      alt="笑顔のメイがスマートフォンから話しかけるWizMateのイメージ"
                      className="relative h-full w-full object-cover object-center"
                      loading="eager"
                      onError={() => setHeroImageFailed(true)}
                    />
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Problem */}
        <section className="mx-auto max-w-6xl px-5 py-12 md:px-8 md:py-16">
          <div className="rounded-[2rem] border border-[#EADBC8] bg-white/75 px-6 py-9 shadow-[0_14px_34px_rgba(166,124,82,0.10)] backdrop-blur-sm md:px-10 md:py-12">
            <SectionEyebrow>PROBLEM</SectionEyebrow>
            <h2 className="mt-3 text-balance text-2xl font-black leading-snug tracking-tight text-[#5D4037] md:text-3xl">
              社長は毎日、いろいろなことを一人で抱えている。
            </h2>
            <div className="mt-6 flex flex-wrap gap-2.5">
              {CONCERNS.map((concern) => (
                <span
                  key={concern}
                  className="rounded-full border border-[#EADBC8] bg-[#FFF9F4] px-4 py-1.5 text-sm font-bold text-[#7B6861]"
                >
                  {concern}
                </span>
              ))}
            </div>
            <div className="mt-7 max-w-3xl space-y-5 text-base leading-9 text-[#6B5851]">
              <p>売上、採用、資金繰り、顧客対応。ひとつ片づけても、次の判断がすぐにやってくる。</p>
              <div className="grid gap-3 sm:grid-cols-3">
                {[
                  "「誰かに相談したい」",
                  "「誰か、ここまで整理しておいてほしい」",
                  "「考えることが多すぎて、頭がパンクしそう」",
                ].map((quote) => (
                  <p
                    key={quote}
                    className="rounded-[1.2rem] border border-[#EADBC8] bg-[#FFF9F4] px-4 py-4 text-sm font-bold leading-7 text-[#7B6861]"
                  >
                    {quote}
                  </p>
                ))}
              </div>
              <p>そんな状態を、ひとりで抱え続けないためのAI社長室です。</p>
            </div>
          </div>
        </section>

        {/* What is WizMate */}
        <section id="about" className="mx-auto max-w-6xl scroll-mt-24 px-5 py-12 md:px-8 md:py-16">
          <div className="grid gap-8 lg:grid-cols-[minmax(0,0.95fr)_minmax(18rem,0.75fr)] lg:items-center">
            <div className="max-w-3xl">
              <SectionEyebrow>WHAT IS WIZMATE</SectionEyebrow>
              <h2 className="mt-3 text-balance text-2xl font-black leading-snug tracking-tight text-[#5D4037] md:text-3xl">
                WizMateは、あなた専用のAI社長室です。
              </h2>
              <p className="mt-4 text-base leading-8 text-[#6B5851]">
                単なるAIチャットサービスではありません。
                <br />
                <br />
                AI専門家を呼び出して会議をしたり、
                <br />
                情報収集をAIエージェントに頼んだり。
                <br />
                <br />
                社長の毎日に必要な専用機能を、
                <br />
                メイが使いやすく案内します。
              </p>
            </div>
            {!aboutImageFailed ? (
              <div className="hidden md:block">
                <div className="relative aspect-[4/3] overflow-hidden rounded-[1.8rem] border border-[#EADBC8] bg-[#FFF8F1] shadow-[0_18px_40px_rgba(166,124,82,0.14)]">
                  <img
                    src={ABOUT_IMAGE_SRC}
                    alt="WizMateで頼める業務画面のイメージ"
                    className="h-full w-full object-cover object-center"
                    loading="lazy"
                    onError={() => setAboutImageFailed(true)}
                  />
                </div>
              </div>
            ) : null}
          </div>
          <div className="mt-9 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {PILLARS.map((pillar) => (
              <article
                key={pillar.title}
                className="rounded-[1.6rem] border border-[#EADBC8] bg-white/80 px-6 py-6 shadow-[0_12px_28px_rgba(166,124,82,0.10)] backdrop-blur-sm"
              >
                <p className="text-[11px] font-black tracking-[0.2em] text-[#C2A488]">{pillar.tag}</p>
                <h3 className="mt-2 text-lg font-black text-[#5D4D46]">{pillar.title}</h3>
                <p className="mt-2 text-sm leading-7 text-[#7B6861]">{pillar.body}</p>
              </article>
            ))}
          </div>
          <div className="mt-5 flex flex-col gap-3 rounded-[1.4rem] border border-[#D9EBD3] bg-[#F4FAF1] px-6 py-5 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3">
              <span className="mt-0.5 flex h-6 w-11 shrink-0 items-center justify-center rounded bg-[#06C755] text-[11px] font-black text-white">
                LINE
              </span>
              <p className="text-sm leading-7 text-[#5E6F57]">
                普段のLINEから、メイとの会話やニュース・レポート通知を受け取れます。
              </p>
            </div>
            <LineFriendLink className="shrink-0 text-sm font-black text-[#1f8f4e] hover:underline">
              LINEで友だち追加 →
            </LineFriendLink>
          </div>
        </section>

        {/* AI壁打ちルーム */}
        <section className="mx-auto max-w-6xl px-5 py-12 md:px-8 md:py-16">
          <div className="rounded-[2rem] border border-[#EADBC8] bg-[linear-gradient(180deg,#FFFFFF_0%,#FFF7F0_100%)] px-6 py-9 shadow-[0_14px_34px_rgba(166,124,82,0.10)] md:px-10 md:py-12">
            <SectionEyebrow>AI壁打ちルーム</SectionEyebrow>
            <h2 className="mt-3 text-2xl font-black leading-snug tracking-tight text-[#5D4037] md:text-3xl">
              ひとりで悩まず、複数の視点で整理する。
            </h2>
            <ol className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {KABEUCHI_STEPS.map((item) => (
                <li
                  key={item.step}
                  className="rounded-[1.5rem] border border-[#EADBC8] bg-white px-5 py-6 shadow-[0_10px_22px_rgba(166,124,82,0.08)]"
                >
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#7C5A4E] text-sm font-black text-white">
                    {item.step}
                  </span>
                  <h3 className="mt-4 text-base font-black text-[#5D4D46]">{item.title}</h3>
                  <p className="mt-2 text-sm leading-7 text-[#7B6861]">{item.body}</p>
                </li>
              ))}
            </ol>
            <p className="mt-7 max-w-3xl text-sm leading-7 text-[#6B5851]">
              AI壁打ちルームは、Web画面でじっくり相談する機能です。
              <br />
              PCでのご利用をおすすめします。スマホでも利用できます。
            </p>
          </div>
        </section>

        {/* 主要機能 */}
        <section id="features" className="mx-auto max-w-6xl scroll-mt-24 px-5 py-12 md:px-8 md:py-16">
          <div className="max-w-3xl">
            <SectionEyebrow>FEATURES</SectionEyebrow>
            <h2 className="mt-3 text-2xl font-black leading-snug tracking-tight text-[#5D4037] md:text-3xl">
              社長の毎日を支える、主要機能。
            </h2>
          </div>
          <div className="mt-9 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map((feature) => (
              <article
                key={feature.title}
                className="flex flex-col rounded-[1.6rem] border border-[#EADBC8] bg-white/80 px-6 py-6 shadow-[0_12px_28px_rgba(166,124,82,0.10)] backdrop-blur-sm"
              >
                <h3 className="text-lg font-black text-[#5D4D46]">{feature.title}</h3>
                {feature.desc ? (
                  <p className="mt-3 text-sm leading-7 text-[#7B6861]">{feature.desc}</p>
                ) : null}
                <ul className="mt-4 space-y-2.5">
                  {feature.points.map((point) => (
                    <li key={point} className="flex gap-2.5 text-sm leading-7 text-[#7B6861]">
                      <span aria-hidden className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#D1A48F]" />
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </section>

        {/* ChatGPTとの違い */}
        <section className="mx-auto max-w-6xl px-5 py-12 md:px-8 md:py-16">
          <div className="max-w-4xl">
            <SectionEyebrow>VS 汎用AIチャット</SectionEyebrow>
            <h2 className="mt-3 text-balance text-2xl font-black leading-snug tracking-tight text-[#5D4037] md:text-3xl">
              ChatGPTと何が違うの？
            </h2>
            <p className="mt-4 max-w-3xl text-pretty text-base leading-8 text-[#6B5851] md:leading-9">
              ChatGPTを置き換えるサービスではありません。WizMateは、「AIに何を聞けばいいか分からない」「便利そうだけど、仕事でどう使えばいいか分からない」という人でも、相談・壁打ち・調査・情報収集を始めやすいように整えたAI社長室です。
            </p>
          </div>
          <div className="mt-8 overflow-x-auto">
            <table className="w-full min-w-[34rem] border-collapse overflow-hidden rounded-[1.4rem] border border-[#EADBC8] bg-white text-left text-sm">
              <thead>
                <tr className="bg-[#F7ECE2]">
                  <th scope="col" className="px-5 py-4 font-black text-[#7C5A4E]">
                    困りごと
                  </th>
                  <th scope="col" className="px-5 py-4 font-bold text-[#8C7A70]">
                    汎用AIチャットだと
                  </th>
                  <th scope="col" className="px-5 py-4 font-black text-[#5D4D46]">
                    WizMateだと
                  </th>
                </tr>
              </thead>
              <tbody>
                {COMPARISON_ROWS.map((row) => (
                  <tr key={row.issue} className="border-t border-[#EFE2D3]">
                    <th scope="row" className="whitespace-nowrap px-5 py-4 font-black text-[#7C5A4E]">
                      {row.issue}
                    </th>
                    <td className="px-5 py-4 leading-7 text-[#8C7A70]">{row.general}</td>
                    <td className="bg-[#FFFBF7] px-5 py-4 font-bold leading-7 text-[#5D4D46]">{row.wizmate}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* こんな人に向いています */}
        <section className="mx-auto max-w-6xl px-5 py-12 md:px-8 md:py-16">
          <div className="max-w-3xl">
            <SectionEyebrow>FOR</SectionEyebrow>
            <h2 className="mt-3 text-2xl font-black leading-snug tracking-tight text-[#5D4037] md:text-3xl">
              こんな人に向いています。
            </h2>
          </div>
          <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {AUDIENCE.map((item) => (
              <div
                key={item}
                className="flex items-start gap-3 rounded-[1.3rem] border border-[#EADBC8] bg-white/80 px-5 py-5 text-sm font-bold leading-7 text-[#5D4D46] shadow-[0_10px_22px_rgba(166,124,82,0.08)]"
              >
                <span aria-hidden className="mt-0.5 text-base text-[#C58B6B]">
                  ✓
                </span>
                <span>{item}</span>
              </div>
            ))}
          </div>
        </section>

        {/* 料金プラン */}
        <section id="plans" className="mx-auto max-w-6xl scroll-mt-24 px-5 py-12 md:px-8 md:py-16">
          <div className="max-w-3xl">
            <SectionEyebrow>PRICING</SectionEyebrow>
            <h2 className="mt-3 text-2xl font-black leading-snug tracking-tight text-[#5D4037] md:text-3xl">
              シンプルな3プラン。
            </h2>
            <p className="mt-4 text-base leading-8 text-[#6B5851]">
              まずはフリープランから。使い方に合わせて、いつでも切り替えられます。
            </p>
          </div>
          <div className="mt-9 grid gap-4 md:grid-cols-3">
            {PLAN_PRICES.map((plan) => {
              const highlighted = plan.key === "standard";
              return (
                <article
                  key={plan.key}
                  className={`flex flex-col rounded-[1.6rem] border px-6 py-7 ${
                    highlighted
                      ? "border-[#7C5A4E] bg-white shadow-[0_18px_40px_rgba(124,90,78,0.18)]"
                      : "border-[#EADBC8] bg-white/80 shadow-[0_12px_28px_rgba(166,124,82,0.10)]"
                  }`}
                >
                  {highlighted ? (
                    <span className="mb-3 w-fit rounded-full bg-[#7C5A4E] px-3 py-1 text-[11px] font-black tracking-wide text-white">
                      おすすめ
                    </span>
                  ) : null}
                  <h3 className="text-lg font-black text-[#5D4D46]">{plan.name}</h3>
                  <p className="mt-2 text-xl font-black text-[#7C5A4E]">{plan.priceLabel}</p>
                  <p className="mt-3 text-sm leading-7 text-[#7B6861]">
                    向いている人：{PLAN_AUDIENCE[plan.key] ?? plan.description}
                  </p>
                </article>
              );
            })}
          </div>
          <div className="mt-7 flex flex-wrap items-center gap-3">
            <Link to="/register" className={primaryButtonClass}>
              無料ではじめる
            </Link>
            <Link to="/plans" className={secondaryButtonClass}>
              料金を詳しく見る
            </Link>
          </div>
        </section>

        {/* 安心して使うために */}
        <section className="mx-auto max-w-6xl px-5 py-12 md:px-8 md:py-16">
          <div className="rounded-[2rem] border border-[#EADBC8] bg-white/75 px-6 py-9 shadow-[0_14px_34px_rgba(166,124,82,0.10)] backdrop-blur-sm md:px-10 md:py-12">
            <SectionEyebrow>SAFETY</SectionEyebrow>
            <h2 className="mt-3 text-2xl font-black leading-snug tracking-tight text-[#5D4037] md:text-3xl">
              重要な判断は、最後に人が決める。その前の整理を、WizMateが支えます。
            </h2>
            <ul className="mt-7 grid gap-3 sm:grid-cols-2">
              {SAFETY_POINTS.map((point) => (
                <li
                  key={point}
                  className="flex gap-3 rounded-[1.2rem] border border-[#EADBC8] bg-[#FFF9F4] px-5 py-4 text-sm leading-7 text-[#6B5851]"
                >
                  <span aria-hidden className="mt-0.5 text-[#C58B6B]">
                    ・
                  </span>
                  <span>{point}</span>
                </li>
              ))}
            </ul>
            <div className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-sm font-bold text-[#7C5A4E]">
              <Link to="/terms" className="underline underline-offset-4 hover:text-[#6D4D42]">
                利用規約
              </Link>
              <Link to="/privacy" className="underline underline-offset-4 hover:text-[#6D4D42]">
                プライバシーポリシー
              </Link>
              <Link to="/commercial-transactions" className="underline underline-offset-4 hover:text-[#6D4D42]">
                特定商取引法に基づく表記
              </Link>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section id="faq" className="mx-auto max-w-3xl scroll-mt-24 px-5 py-12 md:px-8 md:py-16">
          <div className="text-center">
            <SectionEyebrow>FAQ</SectionEyebrow>
            <h2 className="mt-3 text-2xl font-black tracking-tight text-[#5D4037] md:text-3xl">よくある質問</h2>
          </div>
          <div className="mt-8 space-y-3">
            {FAQS.map((faq) => (
              <details
                key={faq.q}
                className="group rounded-[1.3rem] border border-[#EADBC8] bg-white/85 px-6 py-4 shadow-[0_10px_22px_rgba(166,124,82,0.08)] [&_summary::-webkit-details-marker]:hidden"
              >
                <summary className="flex cursor-pointer items-center justify-between gap-4 text-base font-black text-[#5D4D46]">
                  <span>{faq.q}</span>
                  <span
                    aria-hidden
                    className="shrink-0 text-xl font-black text-[#C2A488] transition group-open:rotate-45"
                  >
                    ＋
                  </span>
                </summary>
                <p className="mt-3 text-sm leading-7 text-[#6B5851]">{faq.a}</p>
              </details>
            ))}
          </div>
        </section>

        {/* Final CTA */}
        <section className="mx-auto max-w-6xl px-5 pb-16 pt-4 md:px-8">
          <div className="relative overflow-hidden rounded-[2.2rem] border border-[#EADBC8] bg-[linear-gradient(135deg,#7C5A4E_0%,#A4715A_100%)] px-7 py-12 text-center shadow-[0_22px_48px_rgba(124,90,78,0.28)] md:px-10 md:py-14">
            <div className="pointer-events-none absolute -right-10 -top-10 h-44 w-44 rounded-full bg-white/10 blur-2xl" />
            <h2 className="relative text-2xl font-black leading-snug tracking-tight text-white md:text-3xl">
              まずは無料で、メイに相談してみませんか？
            </h2>
            <p className="relative mx-auto mt-4 max-w-xl text-sm leading-7 text-[#F4E6DC]">
              フリープランあり。月額3,300円から。LINEからもすぐに友だち追加できます。
            </p>
            <div className="relative mt-8 flex flex-wrap items-center justify-center gap-3">
              <Link
                to="/register"
                className="inline-flex items-center justify-center rounded-2xl bg-white px-6 py-3.5 text-sm font-black text-[#7C5A4E] shadow-[0_10px_24px_rgba(0,0,0,0.12)] transition hover:bg-[#FFF4EC] active:scale-[0.99]"
              >
                無料ではじめる
              </Link>
              <LineFriendLink className={lineButtonClass}>
                <LineBadge />
                LINEで友だち追加
              </LineFriendLink>
              <Link
                to="/plans"
                className="inline-flex items-center justify-center rounded-2xl border border-white/70 bg-transparent px-6 py-3.5 text-sm font-black text-white transition hover:bg-white/10 active:scale-[0.99]"
              >
                料金を見る
              </Link>
            </div>
          </div>
        </section>
      </main>

      {/* フッター */}
      <footer className="border-t border-[#EFE2D3] bg-[#FBF3EC]">
        <div className="mx-auto max-w-6xl px-5 py-12 md:px-8">
          <div className="grid gap-10 md:grid-cols-[1.4fr_1fr_1fr_1fr]">
            <div>
              <div className="flex items-center gap-2.5">
                <LogoMark />
              </div>
              <p className="mt-4 max-w-xs text-sm leading-7 text-[#7B6861]">
                社長の毎日に、AI社長室を。相談・壁打ち・情報収集・企業リサーチを、メイに頼めます。
              </p>
            </div>

            <nav aria-label="製品" className="text-sm">
              <p className="font-black text-[#5D4D46]">製品</p>
              <ul className="mt-4 space-y-2.5 font-bold text-[#7B6861]">
                <li>
                  <a href="#about" className="hover:text-[#7C5A4E]">
                    できること
                  </a>
                </li>
                <li>
                  <a href="#features" className="hover:text-[#7C5A4E]">
                    機能
                  </a>
                </li>
                <li>
                  <a href="#plans" className="hover:text-[#7C5A4E]">
                    料金
                  </a>
                </li>
                <li>
                  <a href="#faq" className="hover:text-[#7C5A4E]">
                    よくある質問
                  </a>
                </li>
              </ul>
            </nav>

            <nav aria-label="はじめる" className="text-sm">
              <p className="font-black text-[#5D4D46]">はじめる</p>
              <ul className="mt-4 space-y-2.5 font-bold text-[#7B6861]">
                <li>
                  <Link to="/register" className="hover:text-[#7C5A4E]">
                    無料ではじめる
                  </Link>
                </li>
                <li>
                  <Link to="/login" className="hover:text-[#7C5A4E]">
                    ログイン
                  </Link>
                </li>
                <li>
                  <LineFriendLink className="hover:text-[#7C5A4E]">
                    LINEで友だち追加
                  </LineFriendLink>
                </li>
                <li>
                  <Link to="/plans" className="hover:text-[#7C5A4E]">
                    料金プラン
                  </Link>
                </li>
              </ul>
            </nav>

            <nav aria-label="法務" className="text-sm">
              <p className="font-black text-[#5D4D46]">法務</p>
              <ul className="mt-4 space-y-2.5 font-bold text-[#7B6861]">
                <li>
                  <Link to="/terms" className="hover:text-[#7C5A4E]">
                    利用規約
                  </Link>
                </li>
                <li>
                  <Link to="/privacy" className="hover:text-[#7C5A4E]">
                    プライバシーポリシー
                  </Link>
                </li>
                <li>
                  <Link to="/commercial-transactions" className="hover:text-[#7C5A4E]">
                    特定商取引法に基づく表記
                  </Link>
                </li>
              </ul>
            </nav>
          </div>

          <div className="mt-10 border-t border-[#EADBC8] pt-6">
            <p className="text-xs leading-6 text-[#9C867D]">
              WizMateの出力は判断材料です。正確性・最新性を保証するものではありません。重要な判断では、一次情報や専門家への確認をおすすめします。
            </p>
            <p className="mt-3 text-xs font-bold text-[#9C867D]">© WizMate（株式会社ウィズダムネットワークス）</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
